import React, { useEffect, useMemo, useContext, useState } from "react";
import { Page, SubmitButton,  required, FormGroup, DatePicker } from "components/common";
import { Control, actions } from "react-redux-form";
import { NotificationManager } from "react-notifications";
import { useForm } from "hooks";
import { Row, Col, Alert, Label, Input, Badge} from "reactstrap";
import Select from "react-select";
import moment from "moment";
import { Trans } from "react-i18next";
import numeral from "numeral";
import EmployeeContext from "contexts/EmployeeContext";
import { fileDownload, getBase64 } from "services/util";
import { ApiClient } from "services";
import BackButton from "components/refactoring/BackButton";

export const Edit = ({ t, model = "local", history, match, client }) => {
  const { employee } = useContext(EmployeeContext);
  
  const [attachment, setAttachment] = useState({});
  const [attachmentName, setAttachmentName] = useState("");
  const api_client = ApiClient();

  const [settings, setSettings] = React.useState(null);
  const [types, setTypes] = useState();
  const [accrued, setAccrued] = useState(null)
  const [replacementEmployees, setReplacementEmployees] = useState([]);

  const onMount = async () => {
    if (!employee) return;
    
    let orgId = employee.connected_organisation_id || employee.connected_organisation;
    const settings = await client
      .get(`payroll/${orgId}/settings/1/public`)
      .get("data");
    const types = await client
      .get(`employees/${employee.id}/holiday_requests/types`)
      .get("data");
    
    const accrued = await client
      .get(`employees/${employee.id}/holiday_requests/get_accrued`)
      .get("data");
    
    const employees = await client
      .get(`payroll/${orgId}/employees?status=Active&include_all=false`)
      .get("data").map((x) => ({
        label: x.full_name,
        value: x.id,
      }));

    setAccrued(accrued)
    setTypes(types); 
    setSettings(settings);
    setReplacementEmployees(employees?.filter(emp => emp.value !== employee.id));
  };

  React.useEffect(() => void onMount(), [employee]);

  const { id } = match.params;
  const { dispatch, setValue, Form, value, save, loaded } = useForm({
    model,
  });

  const typeOptions = useMemo(
    () => [
      {
        label: t("Morning"),
        value: "Morning",
      },
      {
        label: t("Afternoon"),
        value: "Afternoon",
      },
    ],
    [t]
  );

  useEffect(() => {
    async function fetchTimeOffer() {
      if (!loaded) return null;
      const { starting, starting_time, ending, ending_time } = value;
      if (!starting || !starting_time || !ending || !ending_time) {
        dispatch(actions.change("local.total_half_days", 0));
      }

      const { total_half_days } = await client
        .post(`employees/${id}/holiday_requests/calculate_time_off`, {
          starting,
          ending,
          starting_time,
          ending_time,
        })
        .get("data");

      dispatch(actions.change("local.total_half_days", total_half_days));
    }
    fetchTimeOffer();
  }, [
    value.starting,
    value.starting_time,
    value.ending,
    value.ending_time,
    loaded,
    id, 
  ]);

  useEffect(() => {
    const today = moment();

    setValue({
      employee: id,
      starting: today.format("YYYY-MM-DD"),
      ending: today.format("YYYY-MM-DD"),
      starting_time: "Morning",
      ending_time: "Afternoon",
      total_half_days: 2,
      reason: "",
      attachment: null,
    });
  }, []);

  const handleFileInputChange = e => {

    const file = e.target.files[0];

    getBase64(file)
      .then(result => {
        file["base64"] = result;
        setAttachment(result)
        setAttachmentName(file.name)
      })
      .catch(err => {
        console.log(err);
      });
  };

 
  function getFileName(file_path) {
    if (file_path) {
      const path_array = file_path.split('/')
      return (<span>{path_array[path_array.length - 1]}<i className="fas fa-arrow-circle-down mx-2"></i></span>)
    }
    return t("No Current File Yet")
  }

  async function onSubmit(item) {
    try {

      if (attachmentName) {
        item = { ...item, 'attachment': { 'file': attachment, 'name': attachmentName } }
      } else if (item.attachment !== null) {
        delete item.attachment
      }

      await save(client, `employees/${id}/holiday_requests`, item);
      NotificationManager.success(t("Time off request has been sent"));
      history.replaceUp({ pathname: "../.." });
    } finally {
      dispatch(actions.setPending(model, false));
    }
  }

  if (!settings || !employee || !loaded) return null;

  return (
    <Page title="Book Time Off">
       <BackButton />
      {accrued &&
        <div className="accrued my-2">
          <Badge color="warning" pill className="py-2" style={{fontSize:"0.7rem"}}> <Trans>Balance</Trans><span className="font-weight-bold">({accrued.current_balance})</span></Badge>
        </div>
      }
      <Form
        onSubmit={onSubmit}
        validators={{
          starting: { required },
          ending: { required },
          starting_time: { required },
          ending_time: { required },
          type: { required },
          replacement_employee: { required },
          total_half_days: (v) => v > 0,
        }}
      >
        <Row>
          <Col>
            <FormGroup label="Starting *" model=".starting" className="py-2">
              <DatePicker className="form-control" model=".starting" />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup
              addControlClass={false}
              label="Starting Time *"
              model=".starting_time"
              className="py-2"
            >
              <Control.text
                model=".starting_time"
                component={Select}
                options={typeOptions}
                simpleValue
                clearable={false}
                searchable={false}
                disabled={!settings.allow_half_day_holidays}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup label="Ending *" model=".ending" className="py-2">
              <DatePicker className="form-control" model=".ending" />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup
              label="Ending Time *"
              model=".ending_time"
              className="py-2"
              addControlClass={false}
            >
              <Control.text
                model=".ending_time"
                component={Select}
                options={typeOptions}
                simpleValue
                clearable={false}
                searchable={false}
                disabled={!settings.allow_half_day_holidays}
              />
            </FormGroup>
          </Col>
        </Row>
        
        <Row>
          <Col>
            <FormGroup label="Type *" model=".type" addControlClass={false} className="pt-2">
              <Control.text
                model=".type"
                component={Select}
                options={types.map(type => ({ ...type, label: t(type.value) }))}
                simpleValue
                clearable={false}
              />
            </FormGroup>
            <div className="alrt-cnt my-1">
              <Alert color={value.total_half_days ? "success" : "warning"} className="py-1" style={{width:"fit-content"}}>
                <Trans>Total days off: </Trans>{" "}
                {numeral(value.total_half_days / 2).format("0[.]0")}
              </Alert>
            </div>
          </Col>
          <Col>
            <FormGroup className="py-2"
              label="Replacement Employee *"
              model=".replacement_employee"
              addControlClass={false}
            >
              <Control.text style={{ backgroundColor: '#F4F7FE' }}
                model=".replacement_employee"
                component={Select}
                options={replacementEmployees}
                simpleValue
                clearable={false}
              />
            </FormGroup>
          </Col>

        </Row>

        <FormGroup className="py-2">
          <Label htmlFor="exampleFile"><Trans>Attachment</Trans></Label>
          <Input id="exampleFile" name="file" type="file" onChange={handleFileInputChange}/>
          <div className="my-1"><span className="mx-1"><Trans>Current file</Trans></span> : <a className="mx-1 link text-success" href="" onClick={(evt) => {evt.preventDefault(); fileDownload(api_client, value.attachment)}}>{getFileName(value.attachment)} </a></div>
        </FormGroup>

        <FormGroup label="Reason" model=".reason">
          <Control.textarea rows="4" model=".reason" updateOn="blur" />
        </FormGroup>
        <div className="d-flex justify-content-end my-3">
          <SubmitButton><Trans>Send Request</Trans></SubmitButton>
        </div>
      </Form>
    </Page>
  );
};

export default Edit;
